/**
 * useAutoPauseOnHidden — switches r3f to stopped frameloop while the page
 * is hidden. Saves CPU when the viewer is in a background tab.
 *
 * Respects the demand-rendering mode: when visible, restores to 'demand'
 * if demand rendering is active, otherwise 'always'.
 *
 * When `enabled = false`, no listener is attached.
 */

import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';

export function useAutoPauseOnHidden(enabled: boolean, demandMode = false): void {
  const setFrameloop = useThree((s) => s.setFrameloop);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const activeMode = demandMode ? 'demand' : 'always';
    const handler = () => {
      setFrameloop(document.hidden ? 'never' : activeMode);
    };
    handler();
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      setFrameloop(activeMode);
    };
  }, [enabled, demandMode, setFrameloop]);
}
